import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Linkedin, Instagram, MessageCircle } from 'lucide-react';
import BoldSignalField from '../BoldSignalField';
import { personalInfo } from '../../mock';

const BoldHeroSection = ({ scrollToSection }) => {
  // Split text for word-by-word animation
  const nameWords = personalInfo.name.split(' ');
  const taglineText = "I turn attention into revenue.";
  const taglineWords = taglineText.split(' ');
  
  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.08,
        delayChildren: 0.2
      }
    }
  };

  const wordVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.6, 0.01, 0.05, 0.95]
      }
    }
  };

  return (
    <section id="home" className="relative min-h-screen flex items-center justify-center px-6 overflow-hidden">
      {/* Bold Signal Field - Mouse Reactive */}
      <BoldSignalField />
      
      <div className="relative z-10 text-center max-w-5xl mx-auto">
        {/* Hero Name - Word by Word with Animated Gradient & Pulsing Glow */}
        <motion.h1
          className="mb-8 gradient-text gradient-text-glow"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          style={{ fontFamily: 'Clash Display, Inter, sans-serif' }}
        >
          {nameWords.map((word, index) => (
            <motion.span
              key={index}
              variants={wordVariants}
              className="gradient-text inline-block mr-6"
            >
              {word}
            </motion.span>
          ))}
        </motion.h1>
        
        {/* Tagline with "attention" highlighted */}
        <motion.p
          className="text-3xl md:text-4xl font-semibold mb-6"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8, duration: 0.6 }}
          style={{ fontFamily: 'Clash Display, Inter, sans-serif' }}
        >
          I turn{' '}
          <span className="gradient-text font-bold">attention</span>
          {' '}into{' '}
          <span className="text-[#EDE9FE]">revenue</span>.
        </motion.p>
        
        {/* Sub description */}
        <motion.p
          className="text-lg md:text-xl text-[#9D97B5] mb-12 max-w-3xl mx-auto leading-relaxed"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1, duration: 0.6 }}
        >
          {personalInfo.description}
        </motion.p>
        
        {/* CTA Buttons */}
        <motion.div
          className="flex flex-col sm:flex-row gap-5 justify-center items-center mb-10"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.2, duration: 0.6 }}
        >
          <motion.button
            onClick={() => scrollToSection('projects')}
            className="btn-primary flex items-center gap-2 group"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.98 }}
          >
            See my work
            <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
          </motion.button>
          
          <motion.button
            onClick={() => scrollToSection('contact')}
            className="btn-accent group"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.98 }}
          >
            Let's talk
          </motion.button>
        </motion.div>
        
        {/* Social Links */}
        <motion.div
          className="flex gap-8 justify-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.4, duration: 0.6 }}
        >
          <motion.a
            href="https://linkedin.com/in/maheen-kehkasha-8a6a9a245"
            target="_blank"
            rel="noopener noreferrer"
            className="text-[#9D97B5] hover:text-[#7C3AED] transition-colors"
            aria-label="LinkedIn"
            whileHover={{ scale: 1.2, y: -2 }}
          >
            <Linkedin size={28} />
          </motion.a>
          <motion.a
            href="https://instagram.com/maheenkehkasha"
            target="_blank"
            rel="noopener noreferrer"
            className="text-[#9D97B5] hover:text-[#FF5C39] transition-colors"
            aria-label="Instagram"
            whileHover={{ scale: 1.2, y: -2 }}
          >
            <Instagram size={28} />
          </motion.a>
          <motion.a
            href={personalInfo.whatsappLink}
            target="_blank"
            rel="noopener noreferrer"
            className="text-[#9D97B5] hover:text-[#22D3A5] transition-colors"
            aria-label="WhatsApp"
            whileHover={{ scale: 1.2, y: -2 }}
          >
            <MessageCircle size={28} />
          </motion.a>
        </motion.div>
      </div>
    </section>
  );
};

export default BoldHeroSection;
